import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBuilding } from '../../contexts/BuildingContext';
import { isManager } from '../../utils/roleCheck';

/**
 * Enhanced Autocomplete component for user mentions with floating positioning
 * Supports full names with spaces using @[Name] format
 * Fixed: Case-insensitive filtering and broader search scope
 */
export default function MentionsAutocomplete({ value, onChange, placeholder = "Write your message...", rows = 3 }) {
  const { user } = useAuth();
  const { currentProperty } = useBuilding();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef(null);

  useEffect(() => {
    if (searchQuery !== null) {
      loadUsers(searchQuery);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery]);

  const loadUsers = async (query) => {
    try {
      const filters = [];
      const trimmedQuery = query.trim().toLowerCase(); // Convert to lowercase for comparison

      // Fetch users based on property/role only (no name filtering)
      // We'll do case-insensitive client-side filtering afterwards
      const isGeneral = !isManager(user?.role);

      if (isGeneral && currentProperty?.id) {
        // For general users: fetch users from their property
        filters.push({
          name: 'property_id',
          op: 'Equal',
          value: Number(currentProperty.id)
        });
      }

      const { data, error } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 50, // Fetch more for client-side filtering
        Filters: filters,
        OrderByField: 'name',
        IsAsc: true
      });

      if (error) throw error;

      let results = data?.List || [];

      // For general users, also fetch staff members
      if (isGeneral) {
        const staffFilters = [
        { name: 'role', op: 'In', value: 'BuildingManager,PropertyManager,SuperAdmin,Administrator' }];


        const { data: staffData } = await window.ezsite.apis.tablePage(70648, {
          PageNo: 1,
          PageSize: 20,
          Filters: staffFilters
        });

        if (staffData?.List?.length > 0) {
          // Merge staff with property users, remove duplicates by id
          const existingIds = new Set(results.map((u) => u.id));
          const uniqueStaff = staffData.List.filter((s) => !existingIds.has(s.id));
          results = [...results, ...uniqueStaff];
        }
      }

      // Client-side case-insensitive filtering
      if (trimmedQuery) {
        results = results.filter((user) =>
        user.name?.toLowerCase().includes(trimmedQuery)
        );
      }

      // Sort by name for better UX
      results.sort((a, b) => {
        const aName = a.name?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';

        // Prioritize names that start with the query
        const aStarts = aName.startsWith(trimmedQuery);
        const bStarts = bName.startsWith(trimmedQuery);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        return aName.localeCompare(bName);
      });

      setSuggestions(results);
      setSelectedIndex(0);
    } catch (error) {
      console.error('[Mentions] Suggestion load failed:', error);
      setSuggestions([]);
    }
  };

  const getCaretCoordinates = (element, position) => {
    const div = document.createElement('div');
    const style = window.getComputedStyle(element);

    const properties = [
    'direction', 'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'fontSizeAdjust',
    'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent', 'textDecoration',
    'letterSpacing', 'wordSpacing', 'tabSize', 'MozTabSize'];


    properties.forEach((prop) => {
      div.style[prop] = style[prop];
    });

    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';

    const content = element.value.substring(0, position);
    div.textContent = content;

    const span = document.createElement('span');
    span.textContent = element.value.substring(position) || '.';
    div.appendChild(span);

    document.body.appendChild(div);
    const { offsetTop, offsetLeft } = span;
    document.body.removeChild(div);

    return {
      top: offsetTop + parseInt(style.lineHeight) - element.scrollTop,
      left: offsetLeft - element.scrollLeft
    };
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;

    onChange(newValue);
    setCursorPosition(newCursorPos);

    const textBeforeCursor = newValue.substring(0, newCursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      // Trigger if @ is at start or after space/newline
      const isTriggered = lastAtIndex === 0 || /[\s\n]/.test(charBeforeAt);
      const isStillTyping = !textAfterAt.includes(' ') && !textAfterAt.includes('\n');

      if (isTriggered && isStillTyping) {
        setSearchQuery(textAfterAt);
        setShowSuggestions(true);

        const coords = getCaretCoordinates(e.target, lastAtIndex);
        setMenuPosition(coords);
        return;
      }
    }

    setShowSuggestions(false);
    setSearchQuery(null);
  };

  const insertMention = (userData) => {
    if (!userData) return;

    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    const beforeMention = value.substring(0, lastAtIndex);

    // Use bracketed format to support names with spaces
    const mention = `@[${userData.name}] `;
    const newValue = beforeMention + mention + textAfterCursor;

    onChange(newValue);
    setShowSuggestions(false);
    setSearchQuery(null);

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = beforeMention.length + mention.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSearchQuery(null);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 focus:bg-white outline-none transition-all resize-none text-gray-700 font-medium" />


      {showSuggestions && suggestions.length > 0 &&
      <div
        className="absolute z-[100] w-64 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-blue-900/20 max-h-56 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200 pointer-events-auto"
        style={{
          top: `${menuPosition.top + 8}px`,
          left: `${Math.min(menuPosition.left, (textareaRef.current?.offsetWidth || 300) - 260)}px`
        }}>

          <div className="p-3 bg-gray-50 border-b border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Mention someone</p>
          </div>
          <div className="overflow-y-auto flex-1">
            {suggestions.map((item, idx) =>
          <button
            key={item.id}
            type="button"
            onClick={() => insertMention(item)}
            onMouseEnter={() => setSelectedIndex(idx)}
            className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-all ${
            idx === selectedIndex ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`
            }>

                <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 shadow-sm ${
            idx === selectedIndex ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-600'}`
            }>
                  {item.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{item.name}</p>
                  <p className={`text-[10px] font-medium uppercase tracking-tighter ${
              idx === selectedIndex ? 'text-white/70' : 'text-gray-400'}`
              }>
                    {item.role || 'Resident'}
                  </p>
                </div>
              </button>
          )}
          </div>
        </div>
      }
    </div>);

}